import { ArrowLeft, ArrowUpDown, Info, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/ui/base/table';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';

import type { ChaveDeDominio, LinhaDeDominio } from './dados';
import { useDominio } from './useDominio';

export interface ColunaDeDominio {
  campo: string | ((linha: LinhaDeDominio) => React.ReactNode);
  titulo: string;
  numerica?: boolean;
  /**
   * Chave usada para ordenar e buscar quando `campo` é função. Sem ela, a
   * coluna derivada não ordena — melhor não ordenar do que ordenar errado.
   */
  ordenarPor?: string;
}

export interface TelaDeDominioProps {
  titulo: string;
  descricao: string;
  chave: ChaveDeDominio;
  colunas: readonly ColunaDeDominio[];
  voltarPara: string;
  rotuloDoVoltar: string;
  /**
   * Destino do detalhe de cada linha. Quando existe, a LINHA INTEIRA vira
   * link — regra 9 das regras de interface do CLAUDE.md: "em listagem com
   * detalhe, a linha inteira é clicável; não existe botão 'Ver'".
   */
  caminhoDaLinha?: (linha: LinhaDeDominio) => string;
  /**
   * Colunas `Ordem` e `Saúde`, que o protótipo tem em toda tabela de domínio.
   * Os cadastros de referência (escolas, seguradoras) não as têm lá, e por isso
   * a opção existe em vez de ser fixa.
   */
  colunasDeControle?: boolean;
  /**
   * Aviso informativo acima da tabela, como o protótipo tem em algumas telas —
   * explica o efeito de um campo sobre o resto do sistema. Só entra quando o
   * protótipo traz um, e nunca com conteúdo de conversa interna.
   */
  aviso?: string;
  /**
   * Concordância de gênero do rótulo de status. A maioria dos domínios é
   * masculina ("Tipo", "Motivo") e usa "Ativo/Inativo"; escolas e seguradoras
   * são femininas no protótipo ("Ativa/Inativa") — daí a opção, em vez de
   * fixar um gênero só.
   */
  situacaoFeminina?: boolean;
}

type Situacao = 'todos' | 'ativo' | 'inativo';

/**
 * Tela de um domínio de parametrização: cabeçalho, filtros, tabela e os quatro
 * estados exigidos pelo CLAUDE.md.
 *
 * Extraída porque vinte telas de `/admin/parametros` diferem apenas em título,
 * origem do dado e colunas — o CLAUDE.md manda extrair componente já na segunda
 * repetição ("Regras de interface", item 1). Melhorar a busca aqui melhora as
 * vinte de uma vez.
 *
 * AS COLUNAS SEGUEM O PROTÓTIPO, no nome e na ordem:
 * `Nome · [campos próprios] · Ordem · Saúde · Status`.
 *
 * DUAS COISAS DO PROTÓTIPO NÃO ESTÃO AQUI, e a razão é a mesma — item 7 das
 * regras de interface do CLAUDE.md: "o que o usuário não pode fazer NÃO É
 * RENDERIZADO — não basta esconder por estilo":
 *
 * - **coluna de seleção**: só serve para ação em lote, que não existe;
 * - **coluna `Ações`** (editar, histórico, inativar) e botão `Novo`: dependem de
 *   API de escrita que não existe nesta fase. Botão que não faz nada é pior que
 *   botão ausente — ensina a pessoa a esperar um efeito que não vem.
 *
 * As duas entram junto com os endpoints, e a ordem então será a do item 10:
 * seleção primeiro, ações por último. Há teste garantindo que hoje não existem.
 *
 * A coluna `Saúde` existe, mas VAZIA. Ela é juízo calculado por regra de
 * validação que ainda não foi construída; preenchê-la com "Saudável" a partir de
 * dado simulado exibiria diagnóstico que ninguém apurou — num sistema público,
 * isso é pior que a lacuna. A estrutura fica, a afirmação não.
 */
export function TelaDeDominio({
  titulo,
  descricao,
  chave,
  colunas,
  voltarPara,
  rotuloDoVoltar,
  caminhoDaLinha,
  colunasDeControle = true,
  aviso,
  situacaoFeminina = false,
}: TelaDeDominioProps) {
  const { estado, recarregar } = useDominio(chave);
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState<Situacao>('todos');
  const [ordem, setOrdem] = useState<{ campo: string; crescente: boolean } | null>(null);

  // Em useMemo porque um array novo a cada render invalidaria a filtragem abaixo,
  // refazendo busca e ordenação a cada tecla digitada.
  const linhas = useMemo(() => (estado.situacao === 'pronto' ? estado.linhas : []), [estado]);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    let resultado = linhas.filter((linha) => {
      const ativa = linha.situacao !== 'inativo';
      if (situacao === 'ativo' && !ativa) return false;
      if (situacao === 'inativo' && ativa) return false;
      if (!termo) return true;
      // Busca em todo valor textual da linha: quem procura "Iveco" não deve
      // precisar saber que a coluna se chama "marca".
      return Object.entries(linha).some(
        ([campo, valor]) =>
          campo !== 'id' &&
          campo !== 'slug' &&
          String(valor ?? '')
            .toLowerCase()
            .includes(termo),
      );
    });

    if (ordem) {
      resultado = [...resultado].sort((a, b) => {
        const x = a[ordem.campo];
        const y = b[ordem.campo];
        if (typeof x === 'number' && typeof y === 'number') {
          return ordem.crescente ? x - y : y - x;
        }
        const comparacao = String(x ?? '').localeCompare(String(y ?? ''), 'pt-BR');
        return ordem.crescente ? comparacao : -comparacao;
      });
    }

    return resultado;
  }, [linhas, busca, situacao, ordem]);

  function alternarOrdem(campo: string) {
    setOrdem((atual) =>
      atual?.campo === campo ? { campo, crescente: !atual.crescente } : { campo, crescente: true },
    );
  }

  const filtrando = busca.trim() !== '' || situacao !== 'todos';

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={voltarPara}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {rotuloDoVoltar}
        </Link>
      </div>

      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">{titulo}</h1>
        <p className="text-sm text-muted-foreground">{descricao}</p>
      </header>

      {aviso && (
        <Alert>
          <Info className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{aviso}</AlertDescription>
        </Alert>
      )}

      {estado.situacao === 'carregando' && (
        <Card>
          <CardContent className="flex items-center justify-center gap-3 py-16">
            <Spinner />
            <p className="text-sm text-muted-foreground">Carregando {titulo.toLowerCase()}…</p>
          </CardContent>
        </Card>
      )}

      {estado.situacao === 'erro' && (
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar"
          description="Tente de novo. Se continuar, avise a administração do sistema."
          primaryAction={{ label: 'Tentar de novo', onClick: recarregar }}
          secondaryAction={{ label: rotuloDoVoltar, to: voltarPara, variant: 'outline' }}
          technicalDetails={estado.mensagem}
        />
      )}

      {estado.situacao === 'vazio' && (
        <EmptyState
          title="Nenhum registro cadastrado"
          description={`${titulo} ainda não tem nenhum registro. Assim que houver, ele aparece aqui.`}
        />
      )}

      {estado.situacao === 'pronto' && (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                placeholder="Buscar nesta lista…"
                aria-label={`Buscar em ${titulo.toLowerCase()}`}
                className="pl-9"
              />
            </div>

            <Select value={situacao} onValueChange={(valor) => setSituacao(valor as Situacao)}>
              <SelectTrigger className="sm:w-52" aria-label="Filtrar por situação">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas as situações</SelectItem>
                <SelectItem value="ativo">Apenas ativos</SelectItem>
                <SelectItem value="inativo">Apenas inativos</SelectItem>
              </SelectContent>
            </Select>

            <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
              {visiveis.length} de {linhas.length} {linhas.length === 1 ? 'registro' : 'registros'}
            </p>
          </div>

          {visiveis.length === 0 ? (
            <EmptyState
              variant="search"
              title="Nenhum registro corresponde"
              description="Nenhum registro atende à busca e ao filtro escolhidos."
              actionLabel="Limpar busca e filtro"
              onAction={() => {
                setBusca('');
                setSituacao('todos');
              }}
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableCaption className="sr-only">{descricao}</TableCaption>
                    <TableHeader>
                      <TableRow>
                        {colunas.map((coluna) => {
                          const campo =
                            typeof coluna.campo === 'string' ? coluna.campo : coluna.ordenarPor;
                          return (
                            <TableHead
                              key={coluna.titulo}
                              className={coluna.numerica ? 'text-right' : undefined}
                              scope="col"
                              aria-sort={
                                // `campo` pode ser indefinido em coluna derivada sem
                                // `ordenarPor`; sem o teste de `ordem` primeiro,
                                // undefined === undefined marcaria a coluna errada.
                                ordem && campo && ordem.campo === campo
                                  ? ordem.crescente
                                    ? 'ascending'
                                    : 'descending'
                                  : undefined
                              }
                            >
                              {campo ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="-ml-2 h-7 gap-1 px-2 font-medium"
                                  onClick={() => alternarOrdem(campo)}
                                >
                                  {coluna.titulo}
                                  <ArrowUpDown className="h-3 w-3" aria-hidden="true" />
                                </Button>
                              ) : (
                                coluna.titulo
                              )}
                            </TableHead>
                          );
                        })}
                        {colunasDeControle && (
                          <>
                            <TableHead scope="col" className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-2 h-7 gap-1 px-2 font-medium"
                                onClick={() => alternarOrdem('ordem')}
                              >
                                Ordem
                                <ArrowUpDown className="h-3 w-3" aria-hidden="true" />
                              </Button>
                            </TableHead>
                            <TableHead scope="col">Saúde</TableHead>
                          </>
                        )}
                        <TableHead scope="col">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="-ml-2 h-7 gap-1 px-2 font-medium"
                            onClick={() => alternarOrdem('situacao')}
                          >
                            Status
                            <ArrowUpDown className="h-3 w-3" aria-hidden="true" />
                          </Button>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visiveis.map((linha, indice) => {
                        const destino = caminhoDaLinha?.(linha);
                        return (
                          <TableRow
                            key={String(linha.id ?? linha.slug ?? indice)}
                            className={destino ? 'cursor-pointer hover:bg-muted/50' : undefined}
                          >
                            {colunas.map((coluna, posicao) => (
                              <TableCell
                                key={coluna.titulo}
                                className={coluna.numerica ? 'text-right tabular-nums' : undefined}
                              >
                                {/*
                                O link cobre a primeira celula e recebe o nome
                                acessivel da linha: assim o teclado alcanca o
                                detalhe uma vez por linha, em vez de uma vez por
                                celula, e o leitor de tela anuncia o destino.
                              */}
                                {destino && posicao === 0 ? (
                                  <Link
                                    to={destino}
                                    className="block font-medium text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                                  >
                                    {typeof coluna.campo === 'function'
                                      ? coluna.campo(linha)
                                      : formatar(linha[coluna.campo])}
                                  </Link>
                                ) : typeof coluna.campo === 'function' ? (
                                  coluna.campo(linha)
                                ) : (
                                  formatar(linha[coluna.campo])
                                )}
                              </TableCell>
                            ))}
                            {colunasDeControle && (
                              <>
                                <TableCell className="text-right tabular-nums">
                                  {formatar(linha.ordem)}
                                </TableCell>
                                {/*
                                Vazia de proposito: ver o comentario no topo do
                                arquivo. Nao ha regra de validacao que a calcule.
                              */}
                                <TableCell className="text-muted-foreground">—</TableCell>
                              </>
                            )}
                            <TableCell>
                              <Badge
                                variant={linha.situacao === 'inativo' ? 'secondary' : 'default'}
                              >
                                {linha.situacao === 'inativo'
                                  ? situacaoFeminina
                                    ? 'Inativa'
                                    : 'Inativo'
                                  : situacaoFeminina
                                    ? 'Ativa'
                                    : 'Ativo'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {filtrando && visiveis.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Mostrando {visiveis.length} de {linhas.length}. A lista está filtrada.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Booleano vira palavra; ausente vira travessão. Número e texto saem como são. */
function formatar(valor: unknown): React.ReactNode {
  if (typeof valor === 'boolean') {
    return valor ? 'Sim' : 'Não';
  }
  if (valor === null || valor === undefined || valor === '') {
    return '—';
  }
  return String(valor);
}
